import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { PauseCircle, Search, Trash2, Play, Calendar, User, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const API = `${window.location.origin}/api`;

const formatIQD = (n) =>
  new Intl.NumberFormat("ar-IQ").format(Math.round(n || 0)) + " د.ع";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ar-IQ", {
      dateStyle: "short",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

export default function HeldInvoicesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/held-invoices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.held || []);
    } catch (e) {
      toast.error("فشل جلب الفواتير المعلقة");
    } finally {
      setLoading(false);
    }
  };

  const resumeInvoice = (item) => {
    // تمرير بيانات الفاتورة إلى POS
    try {
      sessionStorage.setItem("resume_held_invoice", JSON.stringify(item));
    } catch {}
    toast.success("جاري استكمال الفاتورة...");
    navigate("/pos");
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm("هل تريد حذف هذه الفاتورة المعلقة؟")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/held-invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("تم الحذف");
      setItems((arr) => arr.filter((x) => x.id !== id));
    } catch (e) {
      toast.error("فشل الحذف");
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((x) => {
      const hay = [x.customer_name, x.notes, x.employee_name]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [items, search]);

  return (
    <div className="space-y-6" data-testid="held-invoices-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <PauseCircle className="w-6 h-6 text-amber-400" />
            الفواتير المعلقة
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            فواتير محفوظة مؤقتاً — استكملها أو احذفها
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchData}
          className="border-slate-600 text-slate-200 hover:bg-slate-700"
          data-testid="refresh-held-btn"
        >
          <RefreshCw className="w-4 h-4 me-2" />
          تحديث
        </Button>
      </div>

      <Card className="bg-slate-800/60 border-slate-700">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="ابحث باسم العميل أو الملاحظات..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white pr-10"
              data-testid="held-search-input"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-800/60 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center justify-between">
            <span>القائمة ({filtered.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center text-slate-400 py-12">جاري التحميل...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-slate-400 py-12" data-testid="held-empty">
              <PauseCircle className="w-12 h-12 mx-auto mb-3 opacity-40" />
              لا توجد فواتير معلقة حالياً
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((h) => (
                <div
                  key={h.id}
                  className="border border-slate-700 rounded-lg p-4 hover:bg-slate-700/30 transition"
                  data-testid={`held-row-${h.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-white">
                          {h.customer_name || "بدون عميل"}
                        </span>
                        <Badge className="bg-amber-500 text-white">
                          {h.invoice_type === "purchase" ? "مشتريات" : "مبيعات"}
                        </Badge>
                        <Badge variant="outline" className="border-slate-600 text-slate-300">
                          {h.items?.length || 0} صنف
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(h.held_at)}
                        </span>
                        {h.employee_name && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {h.employee_name}
                          </span>
                        )}
                        {h.notes && <span className="italic">📝 {h.notes}</span>}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-emerald-400 font-bold text-lg" data-testid={`held-total-${h.id}`}>
                        {formatIQD(h.total)}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => resumeInvoice(h)}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`resume-held-${h.id}`}
                      >
                        <Play className="w-3.5 h-3.5 me-1" />
                        استكمال
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deleteInvoice(h.id)}
                        className="border-rose-500/40 text-rose-400 hover:bg-rose-500/10"
                        data-testid={`delete-held-${h.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
